import { onMounted, onUnmounted } from 'vue'

interface OrganizationData {
  name: string
  alternateName?: string
  url: string
  logo: string
  description: string
  address?: {
    streetAddress: string
    addressLocality: string
    addressRegion: string
    addressCountry: string
  }
  contactPoint?: Array<{
    telephone: string
    contactType: string
    areaServed: string
    availableLanguage: string[]
  }>
  sameAs?: string[]
}

interface RaffleProductData {
  name: string
  description: string
  image: string
  price: number
  priceCurrency: string
  availability: string
  url: string
  brand?: string
  offers?: {
    price: number
    priceCurrency: string
    availability: string
    url: string
    seller: {
      name: string
    }
  }
}

interface FAQData {
  question: string
  answer: string
}

interface BreadcrumbItem {
  name: string
  item: string
}

class SchemaManager {
  private scriptId = 'schema-ld'
  private schemas: any[] = []

  addSchema(schema: any) {
    this.schemas.push(schema)
    this.updateDOM()
  }

  removeSchema(index: number) {
    this.schemas.splice(index, 1)
    this.updateDOM()
  }

  clearSchemas() {
    this.schemas = []
    this.updateDOM()
  }

  private updateDOM() {
    let script = document.getElementById(this.scriptId)
    
    if (this.schemas.length === 0) {
      if (script) script.remove()
      return
    }

    if (!script) {
      script = document.createElement('script')
      script.id = this.scriptId
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }

    script.textContent = JSON.stringify(this.schemas)
  }
}

const schemaManager = new SchemaManager()

export function useSchema() {
  
  const addOrganizationSchema = (data: OrganizationData) => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': data.name,
      'alternateName': data.alternateName,
      'url': data.url,
      'logo': data.logo,
      'description': data.description,
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': data.address?.streetAddress,
        'addressLocality': data.address?.addressLocality,
        'addressRegion': data.address?.addressRegion,
        'addressCountry': data.address?.addressCountry
      },
      'contactPoint': data.contactPoint?.map(cp => ({
        '@type': 'ContactPoint',
        'telephone': cp.telephone,
        'contactType': cp.contactType,
        'areaServed': cp.areaServed,
        'availableLanguage': cp.availableLanguage
      })),
      'sameAs': data.sameAs,
      'potentialAction': {
        '@type': 'SearchAction',
        'target': `${data.url}?search={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    }
    schemaManager.addSchema(schema)
  }

  const addProductSchema = (raffle: any, currentPrice: number = 4) => {
    if (!raffle) return

    const schema: RaffleProductData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      'name': raffle.title || 'Sorteo Exclusivo',
      'description': raffle.description || 'Participa en este sorteo exclusivo',
      'image': raffle.image_url || raffle.prize_image || 'https://media.lafavoritarifas.com/web/logo.png',
      'price': currentPrice,
      'priceCurrency': 'USD',
      'availability': raffle.status === 'active' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      'url': `${window.location.origin}/sorteo/${raffle.id}`,
      'brand': {
        '@type': 'Brand',
        'name': 'Rifas La Favorita'
      },
      'offers': {
        '@type': 'Offer',
        'price': currentPrice,
        'priceCurrency': 'USD',
        'availability': raffle.status === 'active' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        'url': `${window.location.origin}/sorteo/${raffle.id}`,
        'seller': {
          '@type': 'Organization',
          'name': 'Rifas La Favorita'
        },
        'priceValidUntil': raffle.end_at || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      }
    }
    schemaManager.addSchema(schema)
  }

  const addEventSchema = (raffle: any) => {
    if (!raffle) return

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      'name': raffle.title || 'Sorteo Exclusivo',
      'description': raffle.description || 'Participa en este sorteo exclusivo',
      'image': raffle.image_url || raffle.prize_image || 'https://media.lafavoritarifas.com/web/logo.png',
      'startDate': raffle.draw_date || raffle.end_at || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      'eventStatus': raffle.status === 'active' ? 'https://schema.org/EventScheduled' : 'https://schema.org/EventMovedOnline',
      'eventAttendanceMode': 'https://schema.org/OnlineEventAttendanceMode',
      'location': {
        '@type': 'VirtualLocation',
        'url': window.location.origin
      },
      'organizer': {
        '@type': 'Organization',
        'name': 'Rifas La Favorita',
        'url': window.location.origin
      },
      'offers': {
        '@type': 'Offer',
        'price': raffle.ticket_price || 4,
        'priceCurrency': 'USD',
        'availability': raffle.status === 'active' ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
        'url': `${window.location.origin}/sorteo/${raffle.id}`
      }
    }
    schemaManager.addSchema(schema)
  }

  const addFAQSchema = (faqs: FAQData[]) => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': faqs.map(faq => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer
        }
      }))
    }
    schemaManager.addSchema(schema)
  }

  const addBreadcrumbSchema = (items: BreadcrumbItem[]) => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': items.map((item, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': item.name,
        'item': item.item
      }))
    }
    schemaManager.addSchema(schema)
  }

  const addAggregateRatingSchema = (raffle: any) => {
    if (!raffle) return

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'AggregateRating',
      'ratingValue': raffle.rating || 4.5,
      'reviewCount': raffle.review_count || 100,
      'bestRating': 5,
      'worstRating': 1,
      'itemReviewed': {
        '@type': 'Product',
        'name': raffle.title || 'Sorteo'
      }
    }
    schemaManager.addSchema(schema)
  }

  const initBaseSchemas = () => {
    addOrganizationSchema({
      name: 'Rifas La Favorita',
      alternateName: 'La Favorita Rifas',
      url: 'https://www.lafavoritarifas.com',
      logo: 'https://media.lafavoritarifas.com/web/logo.png',
      description: 'La plataforma líder de sorteos en Venezuela. Gana premios en efectivo, vehículos y más con total seguridad y transparencia. ¡Atrapa tu suerte!',
      address: {
        streetAddress: 'Socopó',
        addressLocality: 'Socopó',
        addressRegion: 'Barinas',
        addressCountry: 'VE'
      },
      contactPoint: [
        {
          telephone: '+58-416-745-2305',
          contactType: 'customer service',
          areaServed: 'VE',
          availableLanguage: ['Es', 'En']
        },
        {
          telephone: '+58-416-135-6686',
          contactType: 'sales',
          areaServed: 'VE',
          availableLanguage: ['Es']
        },
        {
          telephone: '+58-412-320-7976',
          contactType: 'technical support',
          areaServed: 'VE',
          availableLanguage: ['Es']
        }
      ],
      sameAs: [
        'https://instagram.com/rifaslafavorita',
        'https://wa.me/584161356686'
      ]
    })
  }

  const clearAllSchemas = () => {
    schemaManager.clearSchemas()
  }

  return {
    addOrganizationSchema,
    addProductSchema,
    addEventSchema,
    addFAQSchema,
    addBreadcrumbSchema,
    addAggregateRatingSchema,
    initBaseSchemas,
    clearAllSchemas
  }
}
